import { createAxios } from '../lib/axiosFactory';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import { BillableItemIds, BillableItemType } from '../lib/enums/billableItem';
import { SubscriptionResponsePayload } from '../lib/interfaces/subscription';
import { isEmpty, pick } from 'lodash';
import { BillableItem } from '../lib/interfaces/billableItem';
import { AccountStatus } from '../lib/enums/accountStatus';

export const state = {
  subscription: {
    id: undefined,
    status: undefined,
    createdAt: undefined,
    subscriptionItems: [],
    billingCycleDay: undefined,
    nextBillingAt: undefined,
    statusUpdatedAt: undefined,
    cancelledAt: undefined,
    hasUpcomingSubscription: undefined,
    shopifySubscriptionId: undefined,
    updatedInCurrentBillingCycle: undefined,
    isUpcomingSubscriptionTerminated: undefined
  },
  plans: [],
  addons: [],
  contactLimit: null,
  smsLimit: null,
  totalNumberOfContacts: null,
  oneTimePurchases: []
};

export const getters = {
  subscriptionItemsPlan(state: any) {
    return (
      state.subscription?.subscriptionItems.find(
        x => x.billableItemType === BillableItemType.ShopifyPlan
      ) ||
      state.subscription?.subscriptionItems.find(
        x => x.billableItemType === BillableItemType.Plan
      )
    );
  },

  isFreePlan(state: any, getters: any) {
    return (
      getters.subscriptionItemsPlan?.billableItemId ===
      BillableItemIds.ShopifyPlan.ShopifyFreeMonthly
    );
  },

  isPaidPlan(state: any, getters: any) {
    return (
      getters.subscriptionItemsPlan?.billableItemId ===
      BillableItemIds.ShopifyPlan.ShopifyPaidMonthly
    );
  },

  currentPlan(state: any, getters: any): BillableItem | undefined {
    return state.plans?.find(
      ({ id }) => id === getters.subscriptionItemsPlan?.billableItemId
    );
  },

  getPlan: (state: any) => (id): BillableItem | undefined => {
    const plans: BillableItem[] = state.plans;
    return plans.find(x => x.id === id);
  }
};

export const mutations = {
  setSubscription(state: any, subscription: any) {
    state.subscription.status = subscription.status;
    state.subscription.createdAt = subscription.createdAt;
    state.subscription.subscriptionItems = subscription.subscriptionItems;
    state.subscription.billingCycleDay = subscription.billingCycleDay;
    state.subscription.nextBillingAt = subscription.nextBillingAt;
    state.subscription.statusUpdatedAt = subscription.statusUpdatedAt;
    state.subscription.cancelledAt = subscription.cancelledAt;

    state.subscription.shopifySubscriptionId =
      subscription.shopifySubscriptionId;

    state.subscription.isUpcomingSubscriptionTerminated =
      subscription?.upcomingSubscription?.status === AccountStatus.Terminated;
  },

  setSmsLimit(state: any, smsLimit: number): void {
    state.smsLimit = smsLimit;
  },

  setContactLimit(state: any, contactLimit: number): void {
    state.contactLimit = contactLimit;
  },
  setUpdatedInCurrentBillingCycle(
    state: any,
    updatedInCurrentBillingCycle: boolean
  ): void {
    state.updatedInCurrentBillingCycle = updatedInCurrentBillingCycle;
  },

  setTotalNumberOfContacts(state: any, totalNumberOfContacts: number): void {
    state.totalNumberOfContacts = totalNumberOfContacts;
  },

  setPlans(state: any, plans: BillableItem[]): void {
    state.plans = plans;
  },

  setAddons(state: any, addons: BillableItem[]): void {
    state.addons = addons;
  },

  setOneTimePurchases(state: any, oneTimePurchases: BillableItem[]): void {
    state.oneTimePurchases = [...state.oneTimePurchases, ...oneTimePurchases];
  }
};

export const actions = {
  // similar to subscription/loadMySubscription
  async loadMySubscription({ commit }): Promise<ActionResponse> {
    try {
      const response = await createAxios().get<{
        data: SubscriptionResponsePayload;
      }>('/backend/subscriptions-show');

      commit('setSubscription', {
        ...pick(response.data.data, 'id'),
        ...response.data.data.attributes
      });

      commit('setContactLimit', response.data.data.attributes.contactLimit);
      commit(
        'setTotalNumberOfContacts',
        response.data.data.attributes.totalNumberOfContacts
      );
      commit('setSmsLimit', response.data.data.attributes.smsLimit);
      commit(
        'setUpdatedInCurrentBillingCycle',
        response.data.data.attributes.updatedInCurrentBillingCycle
      );

      commit(
        'client/setSubscriptionStatus',
        {
          subscriptionStatus: response.data.data.attributes.status
        },
        { root: true }
      );

      const subscriptionPlanId = response.data.data.attributes.subscriptionItems.find(
        item =>
          item.billableItemType === BillableItemType.ShopifyPlan ||
          item.billableItemType === BillableItemType.Plan
      )?.billableItemId;
      commit(
        'client/setSubscriptionPlanId',
        {
          subscriptionPlanId: subscriptionPlanId
        },
        { root: true }
      );

      commit(
        'client/setSubscriptionSmsCreditsQuantity',
        {
          subscriptionSmsCreditsQuantity: response.data.data.attributes.subscriptionItems?.find(
            item =>
              item.billableItemType === BillableItemType.Addon &&
              Object.values<string>(BillableItemIds.Addon).includes(
                item.billableItemId
              )
          )?.quantity
        },
        { root: true }
      );

      return { success: true, errors: {}, body: response.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadPlans({ commit }): Promise<ActionResponse> {
    try {
      const billableItemsImport = await import(
        `../lib/resources/billableItems`
      );
      const { BillableItems } = billableItemsImport as {
        BillableItems: BillableItem[];
      };
      const plans = BillableItems.filter(item => {
        return item.type === BillableItemType.ShopifyPlan;
      });

      commit('setPlans', plans);

      return { success: true, errors: {}, body: plans };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async loadAddons({ commit }): Promise<ActionResponse> {
    try {
      const billableItemsImport = await import(
        `../lib/resources/billableItems`
      );
      const { BillableItems } = billableItemsImport as {
        BillableItems: BillableItem[];
      };
      const addons = BillableItems.filter(
        item => item.type === BillableItemType.Addon
      );
      commit('setAddons', addons);

      return { success: true, errors: {}, body: addons };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async createShopifySubscription(
    { commit },
    { credits }: { credits: number }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios(process.env.ApiBaseUrl).post(
        '/backend/shopify-subscriptions-create',
        {
          data: {
            type: 'shopifySubscriptionsCreate',
            attributes: {
              credits
            }
          }
        }
      );

      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async cancelShopifySubscription(
    { commit },
    { cancelReason, cancelComments }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios(process.env.ApiBaseUrl).post(
        '/backend/shopify-subscription-cancellations-create',
        {
          data: {
            type: 'subscriptions',
            attributes: {
              cancelReason,
              cancelComments
            }
          }
        }
      );

      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },
  async createShopifyPayAsYouGoCreditPurchase(
    { commit },
    { credits }: { credits: number }
  ): Promise<ActionResponse> {
    try {
      const response = await createAxios(process.env.ApiBaseUrl).post(
        '/backend/shopify-one-time-purchases-create',
        {
          data: {
            type: 'shopifyPurchaseOneTime',
            attributes: {
              credits
            }
          }
        }
      );
      return {
        success: true,
        errors: {},
        body: response.data.data.attributes
      };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
